import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import type { EntityStatus } from '@ms/yammer-data-support';
import { GroupMembershipStatus, GroupPrivacy, GroupState } from '@ms/yammer-graphql';
import createCachedSelector from 're-reselect';

import type { GraphqlErrorReason } from '../../api';
import { isGraphqlApiError } from '../../api';
import { addFavoriteGroupRequestId } from '../../state/requestStatuses/types';
import type { Group, ViewerCommunityRelationship, YammerState } from '../../state/types';
import { createFindById, createFindByIdThrows } from '../findById';
import { getRejectedError, isRejected } from '../requestStatuses/selectors';
import { getCurrentSessionUser } from '../session/selectors';

/**
 * @deprecated PERFORMANCE This returns an entity object, which will be a new object instance and will force re-renders
 * whenever the entity changes. This should be replaced by a `getViewerCommunityRelationshipField` selector, or consumers
 * should use the more specific selectors in this file.
 */
export const findById = createFindById('viewerCommunityRelationships');
/**
 * @deprecated PERFORMANCE This returns an entity object, which will be a new object instance and will force re-renders
 * whenever the entity changes. This should be replaced by a `getViewerCommunityRelationshipFieldThrows` selector, or
 * consumers should use the more specific selectors in this file.
 */
export const findByIdThrows = createFindByIdThrows('viewerCommunityRelationships');

type GetFulfilledViewerCommunityRelationships = (state: YammerState) => Record<string, ViewerCommunityRelationship>;
/**
 * @deprecated PERFORMANCE This is a very bad selector, it selects ALL fulfilled entities and will return
 * a new object any time ANY group relationship in the entire system changes. This should be deleted.
 */
const getFulfilledViewerCommunityRelationships: GetFulfilledViewerCommunityRelationships = (state) =>
  state.viewerCommunityRelationships.fulfilled.byId;

type GetStatusForId = (state: YammerState, groupId: Group['id']) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, groupId) =>
  getStatus(state.viewerCommunityRelationships, groupId);

type ViewerCommunityRelationshipSelector = (state: YammerState, groupId: Group['id']) => boolean;

export const isPrivate: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!(viewerCommunityRelationship && viewerCommunityRelationship.privacy === GroupPrivacy.PRIVATE);
};

export const isDeleted: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!(viewerCommunityRelationship && viewerCommunityRelationship.state === GroupState.DELETED);
};

export const isInaccessible: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!(viewerCommunityRelationship && viewerCommunityRelationship.state === GroupState.INACCESSIBLE);
};

export const isAdmin: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!viewerCommunityRelationship?.viewerIsAdmin;
};

export const canManageGroupExpertRoles: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!viewerCommunityRelationship?.viewerCanManageGroupExpertRoles;
};

export const isPending: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!(
    viewerCommunityRelationship && viewerCommunityRelationship.membershipStatus === GroupMembershipStatus.PENDING
  );
};

export const isJoined: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!(
    viewerCommunityRelationship && viewerCommunityRelationship.membershipStatus === GroupMembershipStatus.JOINED
  );
};

export const isFavorite: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!viewerCommunityRelationship?.isFavorite;
};

export const canStartThread: ViewerCommunityRelationshipSelector = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!viewerCommunityRelationship?.canStartThread;
};

type GetGroupMemberShipStatus = (state: YammerState, groupId: Group['id']) => GroupMembershipStatus;
export const getGroupMembershipStatus: GetGroupMemberShipStatus = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return viewerCommunityRelationship ? viewerCommunityRelationship.membershipStatus : GroupMembershipStatus.NONE;
};

type GetAddFavoriteFailureReason = (state: YammerState) => GraphqlErrorReason;
export const getAddFavoriteFailureReason: GetAddFavoriteFailureReason = (state) => {
  const isAddFavoriteRejected = isRejected(state, 'AddFavoriteGroup', addFavoriteGroupRequestId);

  if (!isAddFavoriteRejected) {
    return 'NONE';
  }

  const error = getRejectedError(state, 'AddFavoriteGroup', addFavoriteGroupRequestId);

  return isGraphqlApiError(error) ? error.reason : 'NONE';
};

type IsDynamic = (state: YammerState, groupId: Group['id']) => boolean;
export const isDynamic: IsDynamic = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!viewerCommunityRelationship?.isDynamic;
};

type IsAllCompanyGroup = (state: YammerState, groupId: Group['id']) => boolean;
export const isAllCompanyGroup: IsAllCompanyGroup = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!viewerCommunityRelationship?.isAllCompanyGroup;
};

type IsThreadstarterRestricted = (state: YammerState, groupId: Group['id']) => boolean;
export const isThreadstarterRestricted: IsThreadstarterRestricted = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return !!viewerCommunityRelationship?.threadstarterRestricted;
};

type IsDynamicRosterMembership = (state: YammerState, groupId: Group['id']) => boolean;
export const isDynamicRosterMembership: IsDynamicRosterMembership = (state, groupId) => {
  const viewerCommunityRelationship = findById(state, groupId);

  return Boolean(
    viewerCommunityRelationship?.dynamicGroupMembershipRosterId &&
    viewerCommunityRelationship?.isDynamicGroupMembershipRosterActive
  );
};

type CanAddPinnedResources = (state: YammerState, groupId: Group['id']) => boolean;
export const canAddPinnedResources: CanAddPinnedResources = (state, groupId) => {
  const isUserGroupAdmin = isAdmin(state, groupId);
  const isAllCompany = isAllCompanyGroup(state, groupId);
  const { isGuest } = getCurrentSessionUser(state);

  return !isGuest && (!isAllCompany || isUserGroupAdmin);
};

type GetFulfilledAllCompanyGroupId = (state: YammerState) => Group['id'] | undefined;
export const getFulfilledAllCompanyGroupId: GetFulfilledAllCompanyGroupId = createCachedSelector(
  getFulfilledViewerCommunityRelationships,
  (fulfilledViewerCommunityRelationships) => {
    const allCompanyGroupFound = Object.values(fulfilledViewerCommunityRelationships).find(
      (eachGroupRelationship) => eachGroupRelationship.isAllCompanyGroup
    );

    return allCompanyGroupFound ? allCompanyGroupFound.id : undefined;
  }
)(() => 'cache');

type GetFulfilledGroupPrivacyStatus = (state: YammerState, groupId?: Group['id']) => GroupPrivacy | undefined;
export const getFulfilledGroupPrivacyStatus: GetFulfilledGroupPrivacyStatus = (state, groupId) => {
  if (!groupId) {
    return;
  }
  const viewerCommunityRelationship = findById(state, groupId);

  return viewerCommunityRelationship?.privacy;
};

type CanChangeAdminRights = (state: YammerState, groupId: Group['id']) => boolean;
export const canChangeAdminRights: CanChangeAdminRights = (state, groupId) => {
  const isViewerAdmin = isAdmin(state, groupId);
  const { isVerifiedNetworkAdmin } = getCurrentSessionUser(state);

  return isVerifiedNetworkAdmin || isViewerAdmin;
};

type CanShowNetworkUsers = (state: YammerState, groupId: Group['id']) => boolean;
export const canShowNetworkUsers: CanShowNetworkUsers = (state, groupId) => {
  const isAllCompany = isAllCompanyGroup(state, groupId);
  const isViewerAdmin = isAdmin(state, groupId);
  const isPrivateGroup = isPrivate(state, groupId);
  const { isGuest } = getCurrentSessionUser(state);
  const isMember = isJoined(state, groupId);
  const isNonAdminOfPrivateGroup = !isViewerAdmin && isPrivateGroup;

  return isMember && !(isAllCompany || isGuest || isNonAdminOfPrivateGroup);
};
